import React, { useState } from "react";
import {
  X,
  TrendingUp,
  Heart,
  HandHeart,
  ChevronUp,
  ChevronDown,
} from "lucide-react";
import { fundraiserStore } from "../../stores/FundraiserStore";
import {
  SearchWrapper,
  SearchInput,
  ClearButton,
  DataTable,
  TableHeader,
  TableRow,
  NoResults,
  EmptyState,
  EmptyStateIcon,
  EmptyStateTitle,
  EmptyStateText,
} from "../shared/StyledComponents";
import styled from "styled-components";
import { IDonation } from "../../interfaces";
import { DonationModel, FundraisersDonationsBoardResponse } from "../..";
import {
  ExportDropDown,
  ExportFormat,
  downloadBlob,
  exportFileName,
  isUsableExportFile,
  showExportError,
} from "@mindmemobile/mm-payment-modules";
import { AuthService, ToastService } from "@mindmemobile/mm-shared-global";
import { FundraiserService } from "../../services";
import { canManageFundraiser } from "../../utils/permissions";

const TopDonorsContainer = styled.div`
  background: linear-gradient(135deg, #f8fafc, #ffffff);
  border-radius: 20px;
  padding: 32px;
  margin-bottom: 32px;
  border: 1px solid rgba(255, 255, 255, 0.5);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
`;

const TopDonorsTitle = styled.h3`
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const TopDonorsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const DonorCard = styled.div`
  background: #ffffff;
  border-radius: 16px;
  padding: 24px;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 20px;
  min-height: 100px;
  min-width: 280px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid #f1f5f9;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  }
`;

const DonorAvatar = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #000000;
  font-weight: 700;
  font-size: 18px;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const DonorInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const DonorName = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const DonorStats = styled.div`
  font-size: 14px;
  color: #64748b;
  font-weight: 500;
`;

const DonorAmount = styled.div`
  font-size: 16px;
  font-weight: 800;
  color: #10b981;
  text-align: right;
  flex-shrink: 0;
`;

const SectionHeaderWithSearch = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
`;

const SectionTitle = styled.h2`
  font-size: 28px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
`;

const SortableHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  transition: color 0.2s ease;

  &:hover {
    color: #10b981;
  }
`;

const SortIcon = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  opacity: 0.6;
`;

const DonorInfoCell = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const DonorAvatarSmall = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #000000;
  font-weight: 700;
  font-size: 14px;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const DonorDetailsCell = styled.div``;

const DonorNameCell = styled.div`
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 4px;
  font-size: 16px;
`;

const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ContactItem = styled.div`
  font-size: 12px;
  color: #64748b;
  font-weight: 500;
`;

const AmountValue = styled.div`
  font-weight: 800;
  color: #1e293b;
  font-size: 16px;
  text-align: right;
`;

const DonationCountValue = styled.div`
  font-weight: normal;
  color: #1e293b;
  font-size: 16px;
`;

const DateTimeValue = styled.div`
  font-size: 16px;
  color: #1e293b;
  font-weight: normal;
`;

const PlaceholderContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 12px 24px 20px;
  min-height: 200px;
`;

const PlaceholderText = styled.p`
  font-size: 16px;
  color: #64748b;
  margin: 0 0 24px 0;
  line-height: 1.5;
`;

const PlaceholderButton = styled.button`
  padding: 12px 24px;
  border: 2px solid #64748b;
  background: transparent;
  color: #64748b;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #64748b;
    color: #ffffff;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const TooltipContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const PromoterCell = styled.div`
  display: inline-block;
  font-size: 16px;
  color: #1e293b;
  font-weight: normal;
  cursor: ${(props) => (props.onClick ? "pointer" : "default")};
  max-width: 150px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.5;
  display: flex;
  align-items: center;
  min-height: 24px;
`;

const PromoterTooltip = styled.div<{ visible: boolean }>`
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.9);
  color: #ffffff;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  white-space: pre-line;
  text-align: center;
  max-width: 500px;
  min-width: 120px;
  z-index: 1000;
  opacity: ${(props) => (props.visible ? 1 : 0)};
  visibility: ${(props) => (props.visible ? "visible" : "hidden")};
  transition: all 0.2s ease;
  pointer-events: none;
  margin-bottom: 8px;

  &::after {
    content: "";
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 4px solid transparent;
    border-top-color: rgba(0, 0, 0, 0.9);
  }
`;

interface TopDonor {
  /* Grouping key. Anonymous and named gifts from the same person are separate rows, so an
     anonymous gift is never shown under (or added to) the donor's name. */
  key: string;
  isAnonymous: boolean;
  name: string;
  totalAmount: number;
  donationCount: number;
  email?: string;
  mobile?: string;
  lastComment?: string;
  lastDonationDate: Date;
  promoterInfo: {
    promoterIds: string[];
    promoterNames: string[];
    displayText: string;
    isMultiple: boolean;
  };
}

type SortField = "name" | "amount" | "donations" | "date" | null;
type SortDirection = "asc" | "desc";

interface FundraiserDonorsTabProps {
  donations: FundraisersDonationsBoardResponse[];
  searchTerm: string;
  onSearchChange: (value: string) => void;
}

export const FundraiserDonorsTab: React.FC<FundraiserDonorsTabProps> = ({
  donations,
  searchTerm,
  onSearchChange,
}) => {
  const [sortField, setSortField] = useState<SortField>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [hoveredPromoter, setHoveredPromoter] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);

  /* The API rolls the one-time donations up per donor the same way this tab does (first name +
     last name + email), honouring the current search box, and streams the file back. */
  const exportDonors = (format: ExportFormat) => {
    if (isExporting) return;
    if (!canManageFundraiser("Export")) return;
    setIsExporting(true);
    FundraiserService.exportDonors(
      AuthService.authData.accountId,
      fundraiserStore.selectedFundraiserId || "",
      format,
      searchTerm
    ).subscribe({
      next: (blob) => {
        // Cleared before the download so a browser that refuses it cannot strand the button.
        setIsExporting(false);
        if (!isUsableExportFile(blob)) {
          ToastService.showError("Error in exporting donors");
          return;
        }
        downloadBlob(blob, exportFileName("fundraiser-donors", format));
      },
      error: (error) => {
        setIsExporting(false);
        showExportError(error, "Error in exporting donors");
      },
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    }).format(date);
  };

  const getInitials = (name: string) => {
    if (!name || name === "Anonymous Donor") {
      return "AD";
    }
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase();
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection(field === "date" ? "desc" : "asc");
    }
  };

  const renderSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return (
        <SortIcon>
          <ChevronUp size={12} />
          <ChevronDown size={12} style={{ marginTop: "-2px" }} />
        </SortIcon>
      );
    }

    return (
      <SortIcon style={{ opacity: 1 }}>
        {sortDirection === "asc" ? (
          <ChevronUp size={16} />
        ) : (
          <ChevronDown size={16} />
        )}
      </SortIcon>
    );
  };

  const getPromoterInfo = (
    donorDonations: FundraisersDonationsBoardResponse[]
  ) => {
    const promoterIds = donorDonations
      .map((d) => d.promoterId)
      .filter((id): id is string => id !== undefined);

    const uniquePromoterIds = Array.from(new Set(promoterIds));

    const promoterNames = uniquePromoterIds.map((id) => {
      const promoter = fundraiserStore.promoters.find((p) => p.id === id);
      return promoter?.displayName || "Unknown";
    });

    if (uniquePromoterIds.length === 0) {
      return {
        promoterIds: [],
        promoterNames: [],
        displayText: "–",
        isMultiple: false,
      };
    }

    if (uniquePromoterIds.length === 1) {
      return {
        promoterIds: uniquePromoterIds || [],
        promoterNames: promoterNames || [],
        displayText: promoterNames[0] || "-",
        isMultiple: false,
      };
    }

    return {
      promoterIds: uniquePromoterIds || [],
      promoterNames: promoterNames || [],
      displayText: `${uniquePromoterIds.length} Promoters`,
      isMultiple: true,
    };
  };

  const renderPromoterCell = (donor: TopDonor) => {
    const isHovered = hoveredPromoter === donor.key;
    // const shouldShowTooltip =
    //   donor.promoterInfo.isMultiple &&
    //   donor.promoterInfo.promoterNames.length > 1;

    // // For multiple promoters, format with line breaks for tooltip
    // const tooltipText = donor.promoterInfo.isMultiple
    //   ? donor.promoterInfo.promoterNames.join(", ")
    //   : donor.promoterInfo.displayText;

    // if (!shouldShowTooltip) {
    //   return <PromoterCell>{donor.promoterInfo.displayText}</PromoterCell>;
    // }

    const shouldShowTooltip = true;
    return (
      <TooltipContainer>
        <PromoterCell
          onMouseEnter={() => setHoveredPromoter(donor.key)}
          onMouseLeave={() => setHoveredPromoter(null)}
        >
          {/* This cell used to print the DONOR's name, so the donor (anonymous or not) showed up
              again as their own promoter. */}
          {donor.promoterInfo.displayText}
        </PromoterCell>
        <PromoterTooltip visible={isHovered && shouldShowTooltip}>
          {donor.promoterInfo.promoterNames.length > 0
            ? donor.promoterInfo.promoterNames.join(", ")
            : donor.promoterInfo.displayText}
        </PromoterTooltip>
      </TooltipContainer>
    );
  };
  // Same person = same first name + last name + email, kept apart by whether the gift was anonymous.
  const getDonorKey = (donation: FundraisersDonationsBoardResponse) =>
    `${donation.isAnonymous ? "anonymous" : "named"}-${donation.firstName || ""}-${donation.lastName || ""}-${donation.donorEmail || ""}`;

  // Calculate unique donors and their aggregated data
  const getUniquedonors = (): TopDonor[] => {
    const donorMap = new Map<string, TopDonor>();

    donations.forEach((donation) => {
      const donorKey = getDonorKey(donation);
      const displayName = donation.getDisplayName();
      const existing = donorMap.get(donorKey);
      if (existing) {
        existing.totalAmount += donation.amount;
        existing.donationCount += 1;
        if (donation.comment && !existing.lastComment) {
          existing.lastComment = donation.comment;
        }
        // Update to latest donation date
        if (donation.timestamp > existing.lastDonationDate) {
          existing.lastDonationDate = donation.timestamp;
        }
        // Update contact info if not already set
        if (!existing.email && donation.donorEmail) {
          existing.email = donation.donorEmail;
        }
        if (!existing.mobile && donation.donorMobile?.phoneNumber) {
          existing.mobile = donation.donorMobile.phoneNumber;
        }
      } else {
        donorMap.set(donorKey, {
          key: donorKey,
          isAnonymous: !!donation.isAnonymous,
          name: displayName,
          totalAmount: donation.amount,
          donationCount: 1,
          email: donation.donorEmail || undefined,
          mobile: donation.donorMobile?.phoneNumber || undefined,
          lastComment: donation.comment,
          lastDonationDate: donation.timestamp,
          promoterInfo: {
            promoterIds: [],
            promoterNames: [],
            displayText: "–",
            isMultiple: false,
          },
        });
      }
    });

    // Calculate promoter info for each donor
    const donors = Array.from(donorMap.values());
    donors.forEach((donor) => {
      const donorDonations = donations.filter(
        (d) => getDonorKey(d) === donor.key
      );
      donor.promoterInfo = getPromoterInfo(donorDonations);
    });

    return donors;
  };

  // Calculate top donors by aggregating donations by donor name
  const getTopDonors = (): TopDonor[] => {
    return getUniquedonors()
      .sort((a, b) => b.totalAmount - a.totalAmount)
      .slice(0, 3);
  };

  const clearSearch = () => {
    onSearchChange("");
  };

  const uniqueDonors = getUniquedonors();

  const filteredDonors = uniqueDonors.filter((donor) => {
    if (!searchTerm) return true;

    const searchLower = searchTerm.toLowerCase();
    // An anonymous row must not be findable by the email/phone it hides.
    return (
      donor.name.toLowerCase().includes(searchLower) ||
      (!donor.isAnonymous &&
        donor.email &&
        donor.email.toLowerCase().includes(searchLower)) ||
      (!donor.isAnonymous && donor.mobile && donor.mobile.includes(searchTerm)) ||
      (donor.lastComment &&
        donor.lastComment.toLowerCase().includes(searchLower))
    );
  });

  const getSortedDonors = () => {
    let sorted = [...filteredDonors];

    if (sortField === "name") {
      sorted = sorted.sort((a, b) => {
        const comparison = a.name
          .toLowerCase()
          .localeCompare(b.name.toLowerCase());
        return sortDirection === "asc" ? comparison : -comparison;
      });
    } else if (sortField === "amount") {
      sorted = sorted.sort((a, b) => {
        const comparison = a.totalAmount - b.totalAmount;
        return sortDirection === "asc" ? comparison : -comparison;
      });
    } else if (sortField === "donations") {
      sorted = sorted.sort((a, b) => {
        const comparison = a.donationCount - b.donationCount;
        return sortDirection === "asc" ? comparison : -comparison;
      });
    } else if (sortField === "date") {
      sorted = sorted.sort((a, b) => {
        const comparison =
          a.lastDonationDate.getTime() - b.lastDonationDate.getTime();
        return sortDirection === "asc" ? comparison : -comparison;
      });
    }

    return sorted;
  };

  const topDonors = getTopDonors();
  const sortedDonors = getSortedDonors();

  // Check if we have any donations to show content
  const hasNoDonations = donations.length === 0;

  return (
    <>
      {!hasNoDonations ? (
        <>
          <TopDonorsContainer>
            <TopDonorsTitle>
              <Heart size={28} color="#ef4444" />
              Top Donors
            </TopDonorsTitle>
            <TopDonorsGrid>
              {topDonors.map((donor) => (
                <DonorCard key={donor.key}>
                  <DonorAvatar>{getInitials(donor.name)}</DonorAvatar>
                  <DonorInfo>
                    <DonorName>{donor.name}</DonorName>
                    <DonorStats>
                      {donor.donationCount} donation
                      {donor.donationCount !== 1 ? "s" : ""}
                    </DonorStats>
                  </DonorInfo>
                  <DonorAmount>{formatCurrency(donor.totalAmount)}</DonorAmount>
                </DonorCard>
              ))}
            </TopDonorsGrid>
          </TopDonorsContainer>

          <SectionHeaderWithSearch>
            <SectionTitle>Donors ({filteredDonors.length})</SectionTitle>
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <SearchWrapper>
                <SearchInput
                  placeholder="Search donors..."
                  value={searchTerm}
                  onChange={(e) => onSearchChange(e.target.value)}
                />
                {searchTerm && (
                  <ClearButton onClick={clearSearch} title="Clear search">
                    <X size={16} />
                  </ClearButton>
                )}
              </SearchWrapper>
              <ExportDropDown onExport={exportDonors} isExporting={isExporting} />
            </div>
          </SectionHeaderWithSearch>

          <DataTable
            style={{ minHeight: sortedDonors.length > 0 ? "500px" : "auto" }}
          >
            <TableHeader columns="2.5fr 0.8fr 1.2fr 1.8fr 1.1fr">
              <SortableHeader onClick={() => handleSort("name")}>
                <span>Name</span>
                {renderSortIcon("name")}
              </SortableHeader>
              <SortableHeader
                onClick={() => handleSort("amount")}
                style={{ textAlign: "right", justifyContent: "flex-end" }}
              >
                <span>Amount</span>
                {renderSortIcon("amount")}
              </SortableHeader>
              <SortableHeader
                onClick={() => handleSort("donations")}
                style={{ textAlign: "center", justifyContent: "center" }}
              >
                <span>Donations</span>
                {renderSortIcon("donations")}
              </SortableHeader>
              <SortableHeader onClick={() => handleSort("date")}>
                <span>Last Donation</span>
                {renderSortIcon("date")}
              </SortableHeader>
              <div>Promoter</div>
            </TableHeader>

            {sortedDonors.length === 0 ? (
              <NoResults>
                {searchTerm
                  ? `No donors found matching "${searchTerm}"`
                  : "No donors found"}
              </NoResults>
            ) : (
              sortedDonors.map((donor) => (
                <TableRow
                  key={donor.key}
                  columns="2.5fr 0.8fr 1.2fr 1.8fr 1.1fr"
                >
                  <DonorInfoCell>
                    <DonorAvatarSmall>
                      {getInitials(donor.name)}
                    </DonorAvatarSmall>
                    <DonorDetailsCell>
                      <DonorNameCell>{donor.name}</DonorNameCell>
                      {/* Contact details stay hidden for an anonymous donor, as on the Donations tab. */}
                      {!donor.isAnonymous && (
                      <ContactInfo>
                        {donor.email && (
                          <ContactItem>{donor.email}</ContactItem>
                        )}
                        {donor.mobile && (
                          <ContactItem>{donor.mobile}</ContactItem>
                        )}
                        {!donor.email && !donor.mobile && (
                          <ContactItem>
                            No contact information provided
                          </ContactItem>
                        )}
                      </ContactInfo>
                      )}
                    </DonorDetailsCell>
                  </DonorInfoCell>

                  <AmountValue>{formatCurrency(donor.totalAmount)}</AmountValue>

                  <DonationCountValue style={{ textAlign: "center" }}>
                    {donor.donationCount}
                  </DonationCountValue>

                  <DateTimeValue>
                    {formatDate(donor.lastDonationDate)}
                  </DateTimeValue>

                  <div>{renderPromoterCell(donor)}</div>
                </TableRow>
              ))
            )}
          </DataTable>
        </>
      ) : (
        <EmptyState>
          <EmptyStateIcon>
            <HandHeart size={40} />
          </EmptyStateIcon>
          <EmptyStateTitle>No Donors</EmptyStateTitle>
          <EmptyStateText>
            There are no donors. Share the fundraiser to start getting
            donations.
          </EmptyStateText>
        </EmptyState>
      )}
    </>
  );
};
